/*
 * Copyright (C) Motorola Solutions, INC.
 * All Rights Reserved.
 */

import { AfterContentInit, Directive, ElementRef, Host, HostBinding, HostListener, Input, OnDestroy, OnInit, Optional, Self } from '@angular/core';
import { CdkOverlayOrigin } from '@angular/cdk/overlay';
import { END, ENTER, ESCAPE, HOME, LEFT_ARROW, RIGHT_ARROW, SPACE } from '@angular/cdk/keycodes';
import { ActiveDescendantKeyManager } from '@angular/cdk/a11y';
import { takeUntil } from 'rxjs/operators';
import { DropdownComponent, DropdownItemComponent } from '@msi/cobalt';

/*
 * TODO This Cobalt Directive has been temporarily cloned from the Cobalt library in order to
 *  fix memory leaks associated with the event emitter subscriptions to 'itemPress' and 'itemHover'
 */

@Directive({
    selector: `[msi-custom-dropdown-trigger-for], [msiCustomDropdownTriggerFor]`,
    exportAs: 'msiCustomDropdownTrigger',
    standalone: true
})
export class CustomDropdownTriggerDirective extends CdkOverlayOrigin implements OnInit, OnDestroy, AfterContentInit {
    /** Active key manager for dropdown items */
    keyManager!: ActiveDescendantKeyManager<DropdownItemComponent>;
    /** Dropdown directive that is active */
    currentActiveTrigger: CustomDropdownTriggerDirective = this;

    /** Msi dropdown */
    @Input('msiCustomDropdownTriggerFor') dropdown: any;
    /** In the case of nested dropdowns, whether this sub-menu should open on hover */
    @Input() openOnHover: boolean = true;

    /** Host class to the CSS styles */
    @HostBinding('class.msi-dropdown-trigger') readonly hostClass = true;
    /** Returns the state if a dropdown is open */
    @HostBinding('class.msi-dropdown-trigger-open') get dropdownOpen(): boolean {
        return this.getDropdown() && this.getDropdown().panelOpen;
    }
    /** Returns the state if a dropdown is closed */
    @HostBinding('class.msi-dropdown-trigger-closed') get dropdownClosed(): boolean {
        return !this.getDropdown() || !this.getDropdown().panelOpen;
    }
    /** Tabindex */
    @HostBinding('tabindex') readonly tabindex: number = 0;

    private _ngAfterContentInitTimeoutId!: ReturnType<typeof setTimeout>;

    /** Click event handler */
    @HostListener('click')
    handleClick(): void {
        if (!this.getDropdown()) {
            return;
        }
        if (this.dropdownItem && this.dropdownItem.disabled) {
            return;
        }
        if (this.getDropdown().panelOpen) {
            this.getDropdown().close();
        } else {
            this.getDropdown().open();
        }
    }

    /** Mouseover event handler */
    @HostListener('mouseover')
    hover(): void {
        if (!this.openOnHover) {
            return;
        }

        if (!this.getDropdown()) {
            return;
        }

        if (this.dropdownItem && this.dropdownItem.disabled) {
            return;
        }

        if (this.parentDropdown) {
            this.getDropdown().open();
        }
    }

    /** Keydown event handler */
    @HostListener('keydown', ['$event'])
    handleKeydown(event: KeyboardEvent) {
        if (!this.getDropdown()) {
            return;
        }

        return this.getDropdown().panelOpen ? this.handleOpenKeydown(event) : this.handleClosedKeydown(event);
    }

    constructor(
        element: ElementRef<HTMLElement>,
        @Optional() @Host() public parentDropdown: DropdownComponent,
        @Optional() @Self() public dropdownItem: DropdownItemComponent
    ) {
        super(element);

        if (this.dropdownItem) {
            // @ts-ignore
            this.dropdownItem.dropdownTrigger = this;
        }

        if (this.parentDropdown) {
            // Memory leak fix:
            this.parentDropdown.itemHover.pipe(takeUntil(this.getDropdown().destroy)).subscribe((item) => {
                if (item !== dropdownItem && this.openOnHover) {
                    this.keyManager.setActiveItem(item);
                    this.parentDropdown.trigger.keyManager.setActiveItem(item);
                    this.getDropdown().close();
                }
            });
        }
    }

    ngOnInit() {
        // Expose trigger and dropdown references through the DOM
        this.elementRef.nativeElement.dropdownRef = this.dropdown;
        this.elementRef.nativeElement.triggerRef = this;

        if (!this.getDropdown()) {
            return;
        }

        this.getDropdown().trigger = this;

        if (this.parentDropdown) {
            this.getDropdown().closeOnClick = this.parentDropdown.closeOnClick;
            // Memory leak fix:
            this.getDropdown().pipe(takeUntil(this.getDropdown().destroy)).itemPress.subscribe((item: DropdownItemComponent) => {
                if (this.parentDropdown.closeOnClick) {
                    this.parentDropdown.close();
                }
                this.parentDropdown.itemPress.emit(item);
            });
        }
    }

    ngAfterContentInit() {
        // The native web components version will not be ready to initialize keyManager yet, wait until overlay is attached
        if (!this.elementRef.nativeElement.ngElementStrategy) {
            this._ngAfterContentInitTimeoutId = setTimeout(() => this.initKeyManager());
        }
    }

    initKeyManager() {
        if (!this.getDropdown()) {
            return;
        }

        this.keyManager = new ActiveDescendantKeyManager<DropdownItemComponent>(this.getDropdown().getItems()).withVerticalOrientation();

        this.keyManager.tabOut.pipe(takeUntil(this.getDropdown().destroy)).subscribe(() => {
            this.getDropdown().focus();
            this.getDropdown().close();
        });

        this.keyManager.change.pipe(takeUntil(this.getDropdown().destroy)).subscribe(() => {
            if (this.keyManager.activeItem) {
                this.keyManager.activeItem.hover.emit();
                const nativeElement = this.keyManager.activeItem.elementRef.nativeElement;
                nativeElement.scrollIntoView();
            }
        });
    }

    /** This function serves as an adapter for Custom Element instances of Dropdown */
    getDropdown() {
        if (this.dropdown && this.dropdown.ngElementStrategy) {
            return this.dropdown.ngElementStrategy.componentRef.instance;
        } else {
            return this.dropdown;
        }
    }

    /** Returns a reference to the DOM element of a sidebar containing this trigger, or null
     * @returns {HTMLElement}
     */
    getParentSidebar(): HTMLElement | null {
        let sidebar = null;
        document.querySelectorAll('.msi-sidebar .msi-dropdown-trigger').forEach((elem: Element) =>
            elem === this.elementRef.nativeElement ? (sidebar = elem) : undefined);
        return sidebar;
    }

    /** Closed keydown handler
     * @param {KeyboardEvent} event
     */
    handleClosedKeydown(event: KeyboardEvent) {
        // we have to use keyCode, because that's what keyManager uses
        const keyCode = event.keyCode;

        switch (keyCode) {
            case SPACE:
            case ENTER:
                if (this.currentActiveTrigger.dropdown.panelOpen) {
                    if (this.currentActiveTrigger.keyManager.activeItem) {
                        this.currentActiveTrigger.keyManager.activeItem.press.emit();
                    } else {
                        this.currentActiveTrigger.dropdown.close();
                    }
                } else {
                    this.currentActiveTrigger.dropdown.open();
                }
                event.preventDefault();
                break;
        }
    }

    /** Open keydown handler
     * @param {KeyboardEvent} event
     */
    handleOpenKeydown(event: KeyboardEvent) {
        // we have to use keyCode, because that's what keyManager uses
        const keyCode = event.keyCode;
        const activeItem = this.currentActiveTrigger.keyManager.activeItem;

        switch (keyCode) {
            case SPACE:
            case ENTER:
                if (activeItem) {
                    if (activeItem.dropdownTrigger) {
                        activeItem.dropdownTrigger.hover();
                        this.currentActiveTrigger.keyManager.setFirstItemActive();
                    } else {
                        activeItem.handleClick();
                    }
                } else {
                    this.currentActiveTrigger.dropdown.close();
                }
                event.preventDefault();
                break;
            case ESCAPE:
                this.currentActiveTrigger.dropdown.close();
                break;
            case LEFT_ARROW:
                if (this.currentActiveTrigger.parentDropdown) {
                    this.currentActiveTrigger.dropdown.close();
                }
                break;
            case RIGHT_ARROW:
                if (activeItem && activeItem.dropdownTrigger) {
                    activeItem.dropdownTrigger.hover();
                    this.currentActiveTrigger.keyManager.setFirstItemActive();
                }
                break;
            case HOME:
                this.currentActiveTrigger.keyManager.setFirstItemActive();
                event.preventDefault();
                break;
            case END:
                this.currentActiveTrigger.keyManager.setLastItemActive();
                event.preventDefault();
                break;
            default:
                this.currentActiveTrigger.keyManager.onKeydown(event);
                break;
        }
    }

    ngOnDestroy() {
        clearTimeout(this._ngAfterContentInitTimeoutId);
        // Memory leak fix:
        this.elementRef.nativeElement.dropdownRef = null;
        this.elementRef.nativeElement.triggerRef = null;
    }
}
